/**
 * API constants for EqualIQ frontend
 * Although this is a constant file, it uses dynamic values for API endpoints to support switching test environments.
*/

import accountsData from './accounts.json';

interface AccountConfig {
  tag: string;
  region: string;
  'auth-exports'?: {
    userPoolId: string;
    userPoolClientId: string;
  };
  'api-id'?: string;
}

interface Accounts {
  [accountId: string]: AccountConfig;
}

const accounts: Accounts = accountsData;

// Check if current environment is nonprod (for showing environment switcher)
export function isNonProdEnvironment(): boolean {
  // Use environment variable to determine if we're in a non-production environment
  // Default to false (production) if the environment variable is not set
  return process.env.REACT_APP_ENVIRONMENT !== 'production';
};

// We use localStorage directly for this since it's only exposed for testing in sync flows.
const BACKEND_ENVIRONMENT_STORAGE_KEY = 'backend-environment';

// Get the selected API Environment tag, defaulting to production
export function getApiEnvironment(): string {
  if (isNonProdEnvironment()) {
    return localStorage.getItem(BACKEND_ENVIRONMENT_STORAGE_KEY) || 'equaliq';
  } else {
    return 'equaliq'; 
  }
};

export function setApiEnvironment(tag: string): void {
  localStorage.setItem(BACKEND_ENVIRONMENT_STORAGE_KEY, tag); 
}

// Function to get account configuration by tag
function getAccountByTag(tag: string): AccountConfig | undefined {
  return Object.values(accounts).find(account => account.tag === tag);
}

// Function to get API endpoint for a given account tag
function getApiEndpoint(tag: string): string {
  const account = getAccountByTag(tag);
  if (!account || !account['api-id']) {
    throw new Error(`No API ID found for account tag: ${tag}`);
  }
  return `https://${account['api-id']}.execute-api.${account.region}.amazonaws.com/prod`;
}

// Function to get Cognito configuration for a given account tag
function getCognitoConfig(tag: string) {
  const account = getAccountByTag(tag);
  if (!account || !account['auth-exports']) {
    throw new Error(`No Cognito configuration found for account tag: ${tag}`);
  }
  return {
    REGION: account.region,
    USER_POOL_ID: account['auth-exports'].userPoolId,
    CLIENT_ID: account['auth-exports'].userPoolClientId,
  };
}

// Dynamic API base URL getter
function getApiBaseUrl(): string {
  return getApiEndpoint(getApiEnvironment());
};

// Dynamic contract endpoints getter
export const getContractEndpoints = () => {
  const baseUrl = getApiBaseUrl();
  return {
    LIST_CONTRACTS: `${baseUrl}/listContracts`,
    GET_CONTRACT: `${baseUrl}/getContract`,
    UPDATE_CONTRACT: `${baseUrl}/updateContract`,
    UPLOAD_URL: `${baseUrl}/uploadURL`,
  };
};

// Dynamic Cognito config getter
export const getCognitoConfiguration = () => {
  return getCognitoConfig(getApiEnvironment());
};

// Async Cognito config getter
export const getCognitoConfigurationAsync = async () => {
  const env = getApiEnvironment();
  return getCognitoConfig(env);
};


export const API_BASE_URL = (() => {
  return getApiBaseUrl();
})();

export const COGNITO = (() => {
  return getCognitoConfiguration();
})();

// Legacy exports for backward compatibility (TODO: Use API Code instead)
export const CONTRACT_ENDPOINTS = (() => {
  return getContractEndpoints();
})();

// Mock API Configuration
/**
 * Centralized delay constant for all mock API calls
 * Set to 30ms for responsive demo experience
 */
export const MOCK_API_DELAY = 30; // milliseconds

